// dataGolfService.js
// Service for fetching data from DataGolf API with 24-hour localStorage caching

const API_KEY = '2b5039be18f95ceb8d1f26409925';
const BASE_URL = 'https://feeds.datagolf.com';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const LEADERBOARD_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes for live leaderboard

// Normalize names by stripping diacritics and special Nordic characters for consistent matching
const normalizeName = (name) => {
  if (!name) return '';
  // First replace special characters that don't decompose with NFD
  const specialChars = { 'ø': 'o', 'Ø': 'O', 'æ': 'ae', 'Æ': 'AE', 'ð': 'd', 'Ð': 'D', 'þ': 'th', 'Þ': 'Th', 'ł': 'l', 'Ł': 'L', 'đ': 'd', 'Đ': 'D' };
  let normalized = name.replace(/[øØæÆðÐþÞłŁđĐ]/g, ch => specialChars[ch] || ch);
  // Then strip combining diacritics (å→a, é→e, ñ→n, etc.)
  return normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

class DataGolfService {

  // ── Cache Helpers ──────────────────────────────────────────────

  _getCacheKey(endpoint) {
    return `dg_cache_${endpoint}`;
  }

  _getFromCache(endpoint) {
    try {
      const cacheKey = this._getCacheKey(endpoint);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age < CACHE_DURATION) {
        const ageMinutes = Math.round(age / 60000);
        console.log(`[Cache HIT] ${endpoint} (age: ${ageMinutes} min)`);
        return data;
      }

      console.log(`[Cache EXPIRED] ${endpoint}`);
      localStorage.removeItem(cacheKey);
      return null;
    } catch (error) {
      console.warn('[Cache] Read error:', error);
      return null;
    }
  }

  _setCache(endpoint, data) {
    try {
      const cacheKey = this._getCacheKey(endpoint);
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      console.log(`[Cache SET] ${endpoint}`);
    } catch (error) {
      console.warn('[Cache] Write error (localStorage may be full):', error);
    }
  }

  _getFromCacheWithDuration(endpoint, duration) {
    try {
      const cacheKey = this._getCacheKey(endpoint);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age < duration) {
        const ageMinutes = Math.round(age / 60000);
        console.log(`[Cache HIT] ${endpoint} (age: ${ageMinutes} min)`);
        return data;
      }

      console.log(`[Cache EXPIRED] ${endpoint}`);
      localStorage.removeItem(cacheKey);
      return null;
    } catch (error) {
      console.warn('[Cache] Read error:', error);
      return null;
    }
  }

  _isCacheValidUntilSunday7PM(endpoint) {
    try {
      const cacheKey = this._getCacheKey(endpoint);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const cachedDate = new Date(timestamp);
      const now = new Date();

      // Find the most recent Sunday at 7 PM
      const lastSunday7PM = new Date(now);
      lastSunday7PM.setHours(19, 0, 0, 0);
      // Walk back to Sunday
      while (lastSunday7PM.getDay() !== 0) {
        lastSunday7PM.setDate(lastSunday7PM.getDate() - 1);
      }
      // If we haven't passed this Sunday's 7 PM yet, go back one more week
      if (now < lastSunday7PM) {
        lastSunday7PM.setDate(lastSunday7PM.getDate() - 7);
      }

      // Cache is valid if it was set after the last Sunday 7 PM
      if (cachedDate > lastSunday7PM) {
        console.log(`[Cache HIT] ${endpoint} (valid until next Sunday 7 PM)`);
        return data;
      }

      console.log(`[Cache EXPIRED] ${endpoint} (past Sunday 7 PM refresh)`);
      localStorage.removeItem(cacheKey);
      return null;
    } catch (error) {
      console.warn('[Cache] Read error:', error);
      return null;
    }
  }

  clearCache() {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('dg_cache_'));
    keys.forEach(k => localStorage.removeItem(k));
    console.log(`[Cache] Cleared ${keys.length} cached API responses`);
  }

  // ── API Methods (all cache-wrapped) ────────────────────────────

  // Fetch skill ratings (values)
  async getSkillRatings() {
    const cached = this._getFromCache('skill-ratings-values');
    if (cached) return cached;

    try {
      const response = await fetch(
        `${BASE_URL}/preds/skill-ratings?display=value&file_format=json&key=${API_KEY}`
      );
      if (!response.ok) throw new Error(`DataGolf API error: ${response.status}`);
      const data = await response.json();
      this._setCache('skill-ratings-values', data);
      return data;
    } catch (error) {
      console.error('Error fetching skill ratings:', error);
      return null;
    }
  }

  // Fetch skill rankings
  async getSkillRankings() {
    const cached = this._getFromCache('skill-ratings-ranks');
    if (cached) return cached;

    try {
      const response = await fetch(
        `${BASE_URL}/preds/skill-ratings?display=rank&file_format=json&key=${API_KEY}`
      );
      if (!response.ok) throw new Error(`DataGolf API error: ${response.status}`);
      const data = await response.json();
      this._setCache('skill-ratings-ranks', data);
      return data;
    } catch (error) {
      console.error('Error fetching skill rankings:', error);
      return null;
    }
  }

  // Fetch both values and rankings combined via Netlify function
  async getSkillData() {
    const cached = this._getFromCache('skill-data');
    if (cached) return cached;

    try {
      console.log('Fetching skill data via Netlify function...');
      const response = await fetch('/.netlify/functions/get-skill-data');
      if (!response.ok) throw new Error(`Netlify function error: ${response.status}`);

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to fetch skill data');

      console.log(`Received data for ${result.playerCount} players`);
      this._setCache('skill-data', result.data);
      return result.data;
    } catch (error) {
      console.error('Error fetching skill data:', error);
      return {};
    }
  }

  // Fetch OWGR rankings
  async getOWGRRankings() {
    const cached = this._getFromCache('owgr-rankings');
    if (cached) return cached;

    try {
      const response = await fetch(
        `${BASE_URL}/field-updates?file_format=json&key=${API_KEY}`
      );
      if (!response.ok) throw new Error(`DataGolf API error: ${response.status}`);
      const data = await response.json();
      this._setCache('owgr-rankings', data);
      return data;
    } catch (error) {
      console.error('Error fetching OWGR rankings:', error);
      return null;
    }
  }

  // Fetch live leaderboard data (2-minute cache for continuous refresh)
  async getLeaderboard() {
    const cached = this._getFromCacheWithDuration('leaderboard', LEADERBOARD_CACHE_DURATION);
    if (cached) return cached;

    try {
      console.log('Fetching live leaderboard...');
      const response = await fetch('/.netlify/functions/get-leaderboard');
      if (!response.ok) throw new Error(`Leaderboard function error: ${response.status}`);
      const data = await response.json();

      // Build name-to-data lookup map (normalized names for consistent matching)
      const playerMap = {};
      (data.players || []).forEach(p => {
        playerMap[normalizeName(p.name)] = { position: p.position, score: p.score, thru: p.thru || '' };
      });

      const result = { tournament: data.tournament, players: playerMap };
      this._setCache('leaderboard', result);
      return result;
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      return { tournament: null, players: {} };
    }
  }

  // Fetch FedEx Cup rankings (refreshes every Sunday at 7 PM)
  async getFedExCupRankings() {
    const cached = this._isCacheValidUntilSunday7PM('fedexcup-rankings');
    if (cached) return cached;

    try {
      console.log('Fetching FedEx Cup rankings...');
      const response = await fetch('/.netlify/functions/get-fedexcup');
      if (!response.ok) throw new Error(`FedEx Cup function error: ${response.status}`);
      const data = await response.json();

      // Build name-to-rank lookup map (normalized names for consistent matching)
      const rankMap = {};
      (data.players || []).forEach(p => {
        rankMap[normalizeName(p.name)] = p.rank;
      });

      this._setCache('fedexcup-rankings', rankMap);
      return rankMap;
    } catch (error) {
      console.error('Error fetching FedEx Cup rankings:', error);
      return {};
    }
  }

  // Fetch OWGR rankings from official OWGR API (refreshes every Sunday at 7 PM)
  async getOWGRRankingsLive() {
    const cached = this._isCacheValidUntilSunday7PM('owgr-rankings-live');
    if (cached) return cached;

    try {
      console.log('Fetching live OWGR rankings...');
      const response = await fetch('/.netlify/functions/get-owgr');
      if (!response.ok) throw new Error(`OWGR function error: ${response.status}`);
      const data = await response.json();

      // Build name-to-rank lookup map (normalized names for consistent matching)
      const rankMap = {};
      (data.players || []).forEach(p => {
        rankMap[normalizeName(p.name)] = p.rank;
      });

      this._setCache('owgr-rankings-live', rankMap);
      return rankMap;
    } catch (error) {
      console.error('Error fetching OWGR rankings:', error);
      return {};
    }
  }

  // Fetch tournament results
  async getTournamentResults(tournamentId) {
    const cacheKey = `tournament-results-${tournamentId}`;
    const cached = this._getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const response = await fetch(
        `${BASE_URL}/preds/pre-tournament?tour=pga&file_format=json&key=${API_KEY}`
      );
      if (!response.ok) throw new Error(`DataGolf API error: ${response.status}`);
      const data = await response.json();
      this._setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching tournament results:', error);
      return null;
    }
  }
}

const dataGolfService = new DataGolfService();
export { normalizeName };
export default dataGolfService;
